import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import CardGrid from '../componentes/CardGrid';
import EditModal from '../componentes/EditModal';
import DeleteConfirm from '../componentes/DeleteConfirm';
import Badge from '../componentes/Badge';
import SearchBar from '../componentes/SearchBar';
import { getImageUrl } from '../utils/imageUrl';
import { Plus, Phone, Clock, AlertCircle } from 'lucide-react';

/* Valores por defecto para los campos obligatorios */
const EMPTY_SOCIO = {
  nombre: '',
  apellido: '',
  nombre_local: '',
  tipo_servicio: 'veterinaria',
  whatsapp: '',
  dias_atencion: '',
  horarios_atencion: '',
  servicio_24h: 0,
  emergencias: 0,
};

const SociosPanel = () => {
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');

  const fetchSocios = async () => {
    try {
      setLoading(true);
      const res = await api.getSocios();
      setSocios(res.data?.data || res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocios();
  }, []);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return socios;
    return socios.filter(
      (s) =>
        s.nombre_local?.toLowerCase().includes(q) ||
        s.tipo_servicio?.toLowerCase().includes(q) ||
        s.dias_atencion?.toLowerCase().includes(q) ||
        s.horarios_atencion?.toLowerCase().includes(q)
    );
  }, [socios, search]);

  // Al editar, mapeamos la imagen a 'imagen' para el modal
  const handleEdit = (s) => {
    const socioEdit = {
      ...s,
      imagen: s.logo_url || s.imagen, // el modal espera 'imagen'
    };
    setSelected(socioEdit);
    setShowEdit(true);
  };

  const handleCreate = () => {
    setSelected(null);
    setShowEdit(true);
  };

  const handleDelete = (id) => {
    setSelected(socios.find((s) => s.id === id));
    setShowDelete(true);
  };

  const handleSave = async (formData) => {
    try {
      let socioId = selected?.id;

      // Mezclar con valores por defecto para evitar campos vacíos que causen error 500
      const payload = {
        ...EMPTY_SOCIO,
        ...formData,
      };

      const nuevaImagen = payload.imagen instanceof File ? payload.imagen : null;
      delete payload.imagen;

      if (selected) {
        await api.updateSocio(selected.id, payload);
        socioId = selected.id;
      } else {
        const res = await api.createSocio(payload);
        socioId = res.data?.id || res.data?.data?.id;
        if (!socioId) throw new Error('No se pudo crear el socio');
      }

      // Subir la imagen si se seleccionó una nueva
      if (nuevaImagen) {
        // 🟢 Usamos el nuevo método uploadSocio
        await api.uploadSocio(socioId, nuevaImagen);
      }

      setShowEdit(false);
      fetchSocios();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteSocio(selected.id);
      setShowDelete(false);
      fetchSocios();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const tipoServicioOptions = [
    { value: 'veterinaria', label: 'Veterinaria' },
    { value: 'paseador', label: 'Paseador' },
    { value: 'petshop', label: 'Pet shop' },
    { value: 'guarderia', label: 'Guardería' },
    { value: 'otro', label: 'Otro' },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Socios comerciales</h1>
        <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
          <Plus size={18} /> Nuevo
        </button>
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Buscar por local, tipo de servicio, días u horarios..."
        />
      </div>

      <CardGrid
        items={filtrados}
        loading={loading}
        error={error}
        emptyMessage="No se encontraron socios."
        columns="repeat(auto-fill, minmax(260px, 1fr))"
        renderCard={(s) => (
          <div
            className="sp-card"
            style={{
              padding: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'box-shadow 0.2s',
              background: '#fff',
            }}
          >
            {/* Logo */}
            <div
              style={{
                height: 130,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
              }}
            >
              {s.logo_url || s.imagen ? (
                <img
                  src={getImageUrl(s.logo_url || s.imagen)}
                  alt={s.nombre_local}
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <span style={{ fontSize: '2rem', opacity: 0.3, fontWeight: 300 }}>🏪</span>
              )}
            </div>

            {/* Contenido */}
            <div
              style={{
                padding: '1rem 1rem 0.8rem',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.4rem',
              }}
            >
              {/* Fila: nombre + badge tipo */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {s.nombre_local || 'Sin nombre'}
                </h3>
                {s.tipo_servicio && (
                  <Badge
                    variant="primary"
                    style={{
                      fontSize: '0.7rem',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.tipo_servicio}
                  </Badge>
                )}
              </div>

              {/* Responsable */}
              {(s.nombre || s.apellido) && (
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: 'var(--sp-text-secondary, #555)',
                  }}
                >
                  {[s.nombre, s.apellido].filter(Boolean).join(' ')}
                </p>
              )}

              {/* Contacto y horarios (en bloque) */}
              <div
                style={{
                  margin: '0.2rem 0 0.4rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  fontSize: '0.82rem',
                  color: 'var(--sp-muted, #666)',
                }}
              >
                {s.whatsapp && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Phone size={14} strokeWidth={1.8} />
                    <span>{s.whatsapp}</span>
                  </span>
                )}
                {(s.dias_atencion || s.horarios_atencion) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.1rem',
                    }}
                  >
                    {s.dias_atencion && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Clock size={14} strokeWidth={1.8} />
                        <span>{s.dias_atencion}</span>
                      </span>
                    )}
                    {s.horarios_atencion && (
                      <span style={{ paddingLeft: '1.8rem', fontSize: '0.78rem' }}>
                        {s.horarios_atencion}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Badges 24h / Emergencias */}
              {(s.servicio_24h === 1 || s.emergencias === 1) && (
                <div
                  style={{
                    display: 'flex',
                    gap: '0.4rem',
                    flexWrap: 'wrap',
                    margin: '0.1rem 0 0.2rem',
                  }}
                >
                  {s.servicio_24h === 1 && (
                    <Badge variant="warning" icon={AlertCircle}>
                      24h
                    </Badge>
                  )}
                  {s.emergencias === 1 && (
                    <Badge variant="danger" icon={AlertCircle}>
                      Emergencias
                    </Badge>
                  )}
                </div>
              )}

              {/* Acciones (siempre al final) */}
              <div
                style={{
                  display: 'flex',
                  gap: '0.4rem',
                  marginTop: 'auto',
                  paddingTop: '0.6rem',
                }}
              >
                <button
                  className="sp-btn sp-btn--light"
                  style={{
                    flex: 1,
                    minHeight: 36,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                  }}
                  onClick={() => handleEdit(s)}
                >
                  Editar
                </button>
                <button
                  className="sp-btn sp-btn--danger"
                  style={{
                    flex: 1,
                    minHeight: 36,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                  }}
                  onClick={() => handleDelete(s.id)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      />

      {showEdit && (
        <EditModal
          title={selected ? 'Editar socio' : 'Crear socio'}
          fields={[
            { name: 'nombre', label: 'Nombre del responsable' },
            { name: 'apellido', label: 'Apellido' },
            { name: 'nombre_local', label: 'Nombre del local' },
            {
              name: 'tipo_servicio',
              label: 'Tipo de servicio',
              type: 'select',
              options: tipoServicioOptions,
            },
            { name: 'whatsapp', label: 'WhatsApp' },
            { name: 'dias_atencion', label: 'Días de atención' },
            { name: 'horarios_atencion', label: 'Horarios de atención' },
            { name: 'servicio_24h', label: 'Servicio 24 horas', type: 'checkbox' },
            { name: 'emergencias', label: 'Emergencias', type: 'checkbox' },
            {
              name: 'imagen',
              label: 'Logo',
              type: 'image',
              accept: 'image/*',
            },
          ]}
          initialValues={selected || EMPTY_SOCIO}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
        />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar al socio ${selected?.nombre_local || ''}?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default SociosPanel;